import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  User,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  getRedirectResult,
} from 'firebase/auth';
import type { UserRole } from '@little-chef/domain';
import { auth, authReady } from '../config/firebase';
import { verifyToken, getMe, logout as apiLogout, fetchCsrfToken } from '../services/authApi';
import { clearDynamicCaches } from '../utils/pwaCache';
import { getTelemetryService } from '../services/telemetry/telemetryService';

interface AuthUser {
  id: string;
  email: string;
  role: UserRole;
  userProviderId: string;
  username: string | null;
  termsAcceptedAt: string | null;
  mailingConsent: boolean;
  weekStartDay: number;
  firebaseUser: User;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  signOut: () => Promise<void>;
  refreshAuthToken: () => Promise<string | null>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function mapToAuthUser(
  user: {
    id: string;
    email: string;
    role: UserRole;
    userProviderId: string;
    username: string | null;
    termsAcceptedAt: string | null;
    mailingConsent: boolean;
    weekStartDay: number;
  },
  firebaseUser: User,
): AuthUser {
  return {
    id: user.id,
    email: user.email,
    role: user.role,
    userProviderId: user.userProviderId,
    username: user.username,
    termsAcceptedAt: user.termsAcceptedAt,
    mailingConsent: user.mailingConsent,
    weekStartDay: user.weekStartDay,
    firebaseUser,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const rehydratedRef = useRef(false);

  useEffect(() => {
    let isActive = true;
    let unsubscribe: (() => void) | undefined;

    const initialize = async () => {
      // Ensure persistence is configured before any redirect/result processing.
      await authReady;

      // Bootstrap CSRF: prime the __csrf cookie before any POST/PUT/PATCH/DELETE.
      // The CsrfGuard on the backend requires this header on all mutating requests.
      try {
        await fetchCsrfToken();
      } catch {
        /* non-critical — will retry on next mutating request */
      }

      // Step 0: US5 Migration — silently migrate legacy localStorage authToken to session cookie.
      // Existing users will be seamlessly upgraded on their next visit without re-authentication.
      const legacyToken = localStorage.getItem('authToken');
      if (legacyToken) {
        try {
          await verifyToken(legacyToken);
          // Migration successful — clean up all legacy auth-related localStorage entries
          localStorage.removeItem('authToken');
          // Remove stale firebase:authUser:* entries (defensive cleanup — T037)
          for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i);
            if (key && key.startsWith('firebase:authUser:')) {
              localStorage.removeItem(key);
            }
          }
        } catch {
          // Legacy token is expired or invalid — clean up and fall through to /auth/me check
          localStorage.removeItem('authToken');
          for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i);
            if (key && key.startsWith('firebase:authUser:')) {
              localStorage.removeItem(key);
            }
          }
        }
      }

      // Step 1: Try to rehydrate session from the __session cookie via GET /auth/me.
      // This covers page refreshes and new tabs where Firebase is in-memory (empty).
      try {
        const meResponse = await getMe();
        if (isActive) {
          // Session cookie is valid — rehydrate user state without Firebase sign-in
          setUser(mapToAuthUser(meResponse.user, null as unknown as User));
          setLoading(false);
          rehydratedRef.current = true;
          try {
            getTelemetryService().identifyUser(meResponse.user.userProviderId);
          } catch {
            /* not initialised */
          }
        }
      } catch {
        // 401 or network error — no valid session, continue to Firebase auth state listener
      }

      // CRITICAL: Process redirect result FIRST, before setting up listener
      try {
        await getRedirectResult(auth);
      } catch (error: unknown) {
        // Ignore transient network errors (common on cold start / offline)
        const code = (error as { code?: string })?.code ?? '';
        if (!code.includes('network-request-failed')) {
          setError(error instanceof Error ? error.message : String(error));
        }
      }

      if (!isActive) {
        return;
      }

      // Step 2: Set up permanent listener for new Firebase sign-ins (e.g. after login page)
      unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        if (!isActive) {
          return;
        }

        // Only process if Firebase actually has a user in memory (new sign-in, not just rehydration)
        if (!firebaseUser) {
          // If we didn't rehydrate above and there's no Firebase user, we're logged out
          if (!rehydratedRef.current) {
            setLoading(false);
            try {
              getTelemetryService().clearUser();
            } catch {
              /* not initialised */
            }
          }
          return;
        }

        setLoading(true);
        setError(null);

        try {
          const idToken = await firebaseUser.getIdToken();

          // Read consent written by Login.tsx signup form (email signup flow)
          const pendingConsentRaw = sessionStorage.getItem('lc_pending_consent');
          let consent: { termsAccepted?: boolean; mailingConsent?: boolean } | undefined;
          if (pendingConsentRaw) {
            try {
              consent = JSON.parse(pendingConsentRaw) as {
                termsAccepted?: boolean;
                mailingConsent?: boolean;
              };
            } catch {
              // ignore malformed data
            }
            sessionStorage.removeItem('lc_pending_consent');
          }

          // POST /auth/verify exchanges the ID token for an HttpOnly session cookie.
          // The browser stores the cookie automatically — no localStorage needed.
          const response = await verifyToken(idToken, consent);

          setUser(mapToAuthUser(response.user, firebaseUser));
          try {
            getTelemetryService().identifyUser(firebaseUser.uid);
          } catch {
            /* service not yet initialised in tests */
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Authentication failed');
          setUser(null);
        }

        setLoading(false);
      });
    };

    initialize();

    return () => {
      isActive = false;
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const refreshAuthToken = async (): Promise<string | null> => {
    try {
      const currentUser = auth.currentUser;

      if (!currentUser) {
        return null;
      }

      // Force token refresh and re-establish session cookie via /auth/verify
      const newToken = await currentUser.getIdToken(true);
      await verifyToken(newToken);
      return newToken;
    } catch {
      // Clear auth state if refresh fails
      await signOut();
      return null;
    }
  };

  const refreshUser = async (): Promise<void> => {
    const firebaseUser = auth.currentUser;
    if (firebaseUser) {
      const idToken = await firebaseUser.getIdToken();
      const response = await verifyToken(idToken);
      setUser(mapToAuthUser(response.user, firebaseUser));
      return;
    }
    // No Firebase user in memory (inMemoryPersistence + cookie-rehydrated
    // session, the common case after a page load) — refetch via the
    // session cookie instead, same as initial rehydration.
    const meResponse = await getMe();
    setUser(mapToAuthUser(meResponse.user, null as unknown as User));
  };

  const signOut = async () => {
    try {
      await apiLogout(); // clears __session + __csrf cookies server-side
      await clearDynamicCaches();
      await firebaseSignOut(auth);
      setUser(null);
    } catch (err) {
      throw err;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, error, signOut, refreshAuthToken, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access auth context
 * Must be used within AuthProvider
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
